import { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams, useSearchParams } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Box, CircularProgress } from "@mui/material";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { ToastProvider } from "@/components/Toast";
import Guard from "@/components/Guard";
import AppShell from "@/components/AppShell";
import LoginPage from "@/pages/LoginPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import JoinPartnerPage from "@/pages/JoinPartnerPage";
import PartnerRegisterPage from "@/pages/PartnerRegisterPage";
import RedeemPage from "@/pages/RedeemPage";
import VerifyPage from "@/pages/VerifyPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import MfaRequiredPage from "@/pages/MfaRequiredPage";
import AccountPage from "@/pages/AccountPage";
import ConnectAppPage from "@/features/mcp/ConnectAppPage";
import OverviewPage from "@/pages/OverviewPage";
import PartnerOverviewPage from "@/pages/PartnerOverviewPage";
import { queryClient } from "@/lib/query";
import { ColorModeProvider } from "@/brand/ColorModeContext";

// Each area is its own chunk: an operator who only ever opens Demos should
// not download the document editor.
const ProductionPage = lazy(() => import("@/features/production/ProductionPage"));
const InstanceDetailPage = lazy(() => import("@/features/instances/InstanceDetailPage"));
const JobsPage = lazy(() => import("@/features/jobs/JobsPage"));
const DemosPage = lazy(() => import("@/features/demos/DemosPage"));
const DemoDetailPage = lazy(() => import("@/features/demos/DemoDetailPage"));
const ClustersPage = lazy(() => import("@/features/clusters/ClustersPage"));
const PacksPage = lazy(() => import("@/features/packs/PacksPage"));
const CloudTenantsPage = lazy(() => import("@/features/cloud/TenantsPage"));
const CloudPricingPage = lazy(() => import("@/features/cloud/PricingPage"));
const TrialsPage = lazy(() => import("@/features/trials/TrialsPage"));
const TrialDetailPage = lazy(() => import("@/features/trials/TrialDetailPage"));
const LicensesPage = lazy(() => import("@/features/licenses/LicensesPage"));
const CustomerDetailPage = lazy(() => import("@/features/licenses/CustomerDetailPage"));
const CatalogPage = lazy(() => import("@/features/licenses/CatalogPage"));
const ReleasesPage = lazy(() => import("@/features/releases/ReleasesPage"));
const MarketplacePage = lazy(() => import("@/features/marketplace/MarketplacePage"));
const InstallProfilesPage = lazy(() => import("@/features/marketplace/InstallProfilesPage"));
const FleetPage = lazy(() => import("@/features/fleet/FleetPage"));
const PartnersPage = lazy(() => import("@/features/partners/PartnersPage"));
const PartnerDetailPage = lazy(() => import("@/features/partners/PartnerDetailPage"));
const FilesPage = lazy(() => import("@/features/files/FilesPage"));
const ProjectFilesPage = lazy(() =>
  import("@/features/files/FilesPage").then((m) => ({ default: m.ProjectFilesPage })));
const EditorPage = lazy(() => import("@/features/files/EditorPage"));
const TemplatesPage = lazy(() => import("@/features/templates/TemplatesPage"));
const SalesPage = lazy(() => import("@/features/sales/SalesPage"));
const OpportunityPage = lazy(() => import("@/features/sales/OpportunityPage"));
const CrmLayout = lazy(() => import("@/features/crm/CrmLayout"));
const CrmBoardPage = lazy(() => import("@/features/crm/CrmBoardPage"));
const AllPartnersBoardPage = lazy(() => import("@/features/crm/AllPartnersBoardPage"));
const AccountsPage = lazy(() => import("@/features/crm/AccountsPage"));
const ContactsPage = lazy(() => import("@/features/crm/ContactsPage"));
const DealsPage = lazy(() => import("@/features/crm/DealsPage"));
const AccountDetailPage = lazy(() => import("@/features/crm/AccountDetailPage"));
const DealDetailPage = lazy(() => import("@/features/crm/DealDetailPage"));
const PipelineStagesPage = lazy(() => import("@/features/crm/PipelineStagesPage"));
const CrmTasksPage = lazy(() => import("@/features/crm/TasksPage"));
const CrmLeadsPage = lazy(() => import("@/features/crm/LeadsPage"));
const CrmForecastPage = lazy(() => import("@/features/crm/ForecastPage"));
const ReportsPage = lazy(() => import("@/features/reports/ReportsPage"));
const KbPage = lazy(() => import("@/features/kb/KbPage"));
const AssistantPage = lazy(() => import("@/features/assistant/AssistantPage"));
const AiPage = lazy(() => import("@/features/ai/AiPage"));
const AdminPage = lazy(() => import("@/features/admin/AdminPage"));
const AuditLogPage = lazy(() => import("@/features/admin/AuditLogPage"));
const TicketsPage = lazy(() => import("@/features/tickets/TicketsPage"));
const TicketDetailPage = lazy(() => import("@/features/tickets/TicketDetailPage"));
const ArticlesPage = lazy(() => import("@/features/support/ArticlesPage"));
const NotificationsPage = lazy(() => import("@/features/support/NotificationsPage"));
const CompliancePage = lazy(() => import("@/features/support/CompliancePage"));
const TrainingPage = lazy(() => import("@/features/training/TrainingPage"));
const CertificationPage = lazy(() => import("@/features/certification/CertificationPage"));
const PrivacyPage = lazy(() => import("@/features/privacy/PrivacyPage"));
const KeystorePage = lazy(() => import("@/features/security/KeystorePage"));
const CoursePage = lazy(() => import("@/features/training/CoursePage"));
const LessonPage = lazy(() => import("@/features/training/LessonPage"));

function Centred() {
  return (
    <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
      <CircularProgress />
    </Box>
  );
}

/** A route plus the permissions it needs, so a URL cannot outrun the nav. */
function Screen({ perms, children }: { perms: string[]; children: React.ReactNode }) {
  return (
    <Guard perms={perms}>
      <Suspense fallback={<Centred />}>{children}</Suspense>
    </Guard>
  );
}

function DevReportRedirect() {
  const { ref = "" } = useParams();
  return <Navigate to={`/support/tickets/${ref}`} replace />;
}

/** The old all-tenants list is two modules now; send a bookmark to the one it can open. */
function InstancesHome() {
  const { can } = useAuth();
  return <Navigate to={can("production.read") ? "/production" : "/demos"} replace />;
}

/** Signed out on a page that should come back after sign-in: remember it in ?next=. */
function BackAfterSignIn() {
  const location = useLocation();
  const next = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`/login?next=${next}`} replace />;
}

/** Signed in at /login: go where ?next= says if it is an AI app's sign-in, else home. */
function AfterSignIn() {
  const [params] = useSearchParams();
  const next = params.get("next") ?? "";
  return <Navigate to={next.startsWith("/connect/app?") ? next : "/"} replace />;
}

function Routed() {
  const { me, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!me) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* The two doors a person without an account comes through: an
            invitation from their organisation, or a partner sign-up. */}
        <Route path="/invite/:invitationId" element={<AcceptInvitePage />} />
        <Route path="/register" element={<PartnerRegisterPage />} />
        <Route path="/join/:linkId" element={<JoinPartnerPage />} />
        {/* The card in the scanner box points here. */}
        <Route path="/redeem" element={<RedeemPage />} />
        {/* Public certificate verification (ADR 0020): no sign-in required. */}
        <Route path="/verify/:credentialId" element={<VerifyPage />} />
        {/* An AI app's sign-in: come back here once signed in (console.mcp.oauth). */}
        <Route path="/connect/app" element={<BackAfterSignIn />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // The server refuses every other route for this account, so showing the
  // application would only render a wall of 403s.
  if (me.must_change_password) return <ChangePasswordPage />;
  // Likewise for MultiDoc staff past the 2FA deadline: the server allows only
  // the profile and the 2FA setup until they have it.
  if (me.staff_mfa_setup_due) return <MfaRequiredPage />;

  return (
    <Routes>
      <Route path="/login" element={<AfterSignIn />} />
      <Route path="/connect/app" element={<ConnectAppPage />} />
      {/* A signed-in partner testing the card on the box still gets the page. */}
      <Route path="/redeem" element={<RedeemPage />} />
      {/* Verify stays public even for signed-in users (e.g. following a QR). */}
      <Route path="/verify/:credentialId" element={<VerifyPage />} />
      <Route element={<AppShell />}>
        {/* A partner's landing page is a different screen, not the vendor
            operations queue with half of it hidden. */}
        <Route
          index
          element={me.org.kind === "reseller" ? <PartnerOverviewPage /> : <OverviewPage />}
        />

        {/* Demos and production are separate modules over one tenant record.
            The detail page sends a tenant to its own module, so an old
            /instances/<slug> link still lands in the right place. */}
        <Route path="production" element={<Screen perms={["production.read"]}><ProductionPage /></Screen>} />
        <Route path="production/:slug" element={<Screen perms={["production.read", "instances.read"]}><InstanceDetailPage /></Screen>} />
        <Route path="instances" element={<InstancesHome />} />
        <Route path="instances/:slug" element={<Screen perms={["instances.read"]}><InstanceDetailPage /></Screen>} />
        <Route path="jobs" element={<Screen perms={["jobs.read"]}><JobsPage /></Screen>} />

        <Route path="demos" element={<Screen perms={["demos.read"]}><DemosPage /></Screen>} />
        <Route path="demos/live/:slug" element={<Screen perms={["instances.read"]}><InstanceDetailPage /></Screen>} />
        <Route path="demos/:id" element={<Screen perms={["demos.read"]}><DemoDetailPage /></Screen>} />
        <Route path="packs" element={<Screen perms={["packs.read"]}><PacksPage /></Screen>} />
        <Route path="trials" element={<Screen perms={["trials.read"]}><TrialsPage /></Screen>} />
        <Route path="trials/:id" element={<Screen perms={["trials.read"]}><TrialDetailPage /></Screen>} />
        <Route path="cloud/tenants" element={<Screen perms={["cloud.read"]}><CloudTenantsPage /></Screen>} />
        <Route path="cloud/pricing" element={<Screen perms={["cloud.read"]}><CloudPricingPage /></Screen>} />
        <Route path="clusters" element={<Screen perms={["clusters.read"]}><ClustersPage /></Screen>} />

        <Route path="licenses" element={<Screen perms={["licenses.read"]}><LicensesPage /></Screen>} />
        <Route path="licenses/catalog" element={<Screen perms={["licenses.read"]}><CatalogPage /></Screen>} />
        <Route path="licenses/:customerId" element={<Screen perms={["licenses.read"]}><CustomerDetailPage /></Screen>} />
        <Route path="releases" element={<Screen perms={["releases.read"]}><ReleasesPage /></Screen>} />
        <Route path="marketplace" element={<Screen perms={["releases.read"]}><MarketplacePage /></Screen>} />
        <Route path="install-profiles" element={<Screen perms={["releases.read"]}><InstallProfilesPage /></Screen>} />
        <Route path="fleet" element={<Screen perms={["fleet.read"]}><FleetPage /></Screen>} />

        <Route path="support" element={<Navigate to="/support/tickets" replace />} />
        <Route path="support/tickets" element={<Screen perms={["support.read"]}><TicketsPage /></Screen>} />
        <Route path="support/tickets/:ref" element={<Screen perms={["support.read"]}><TicketDetailPage /></Screen>} />
        {/* Dev reports became the one ticket system (2026-10-27): old links still land. */}
        <Route path="support/dev-reports" element={<Navigate to="/support/tickets" replace />} />
        <Route path="support/dev-reports/:ref" element={<DevReportRedirect />} />
        <Route path="support/articles" element={<Screen perms={["support.read"]}><ArticlesPage /></Screen>} />
        <Route path="support/notifications" element={<Screen perms={["support.read"]}><NotificationsPage /></Screen>} />
        <Route path="support/compliance" element={<Screen perms={["support.read"]}><CompliancePage /></Screen>} />

        <Route path="partners" element={<Screen perms={["resellers.read"]}><PartnersPage /></Screen>} />
        <Route path="partners/:orgId" element={<Screen perms={["resellers.read"]}><PartnerDetailPage /></Screen>} />

        <Route path="files" element={<Screen perms={["files.read", "sales.read"]}><FilesPage /></Screen>} />
        <Route path="files/project/:opportunityId/*" element={<Screen perms={["files.read", "sales.read"]}><ProjectFilesPage /></Screen>} />
        {/* Old links into the raw tree: files are reached by project now. */}
        <Route path="files/*" element={<Navigate to="/files" replace />} />
        <Route path="edit/:nodeId" element={<Screen perms={["files.read"]}><EditorPage /></Screen>} />
        <Route path="templates" element={<Screen perms={["files.read"]}><TemplatesPage /></Screen>} />

        <Route path="sales" element={<Screen perms={["sales.read"]}><SalesPage /></Screen>} />
        <Route path="sales/:id" element={<Screen perms={["sales.read"]}><OpportunityPage /></Screen>} />

        {/* Sales CRM (ADR 0011). crm.account.view gates the whole section. */}
        <Route path="crm" element={<Screen perms={["crm.account.view"]}><CrmLayout /></Screen>}>
          <Route index element={<Screen perms={["crm.deal.view"]}><CrmBoardPage /></Screen>} />
          <Route path="all" element={<Screen perms={["crm.deal.view"]}><AllPartnersBoardPage /></Screen>} />
          <Route path="accounts" element={<Screen perms={["crm.account.view"]}><AccountsPage /></Screen>} />
          <Route path="accounts/:accountId" element={<Screen perms={["crm.account.view"]}><AccountDetailPage /></Screen>} />
          <Route path="contacts" element={<Screen perms={["crm.contact.view"]}><ContactsPage /></Screen>} />
          <Route path="deals" element={<Screen perms={["crm.deal.view"]}><DealsPage /></Screen>} />
          <Route path="deals/:dealId" element={<Screen perms={["crm.deal.view"]}><DealDetailPage /></Screen>} />
          <Route path="tasks" element={<Screen perms={["crm.deal.view"]}><CrmTasksPage /></Screen>} />
          <Route path="leads" element={<Screen perms={["crm.lead.view"]}><CrmLeadsPage /></Screen>} />
          <Route path="forecast" element={<Screen perms={["crm.forecast.view"]}><CrmForecastPage /></Screen>} />
          <Route path="pipeline" element={<Screen perms={["crm.pipeline.manage"]}><PipelineStagesPage /></Screen>} />
        </Route>

        <Route path="reports" element={<Screen perms={["reports.read"]}><ReportsPage /></Screen>} />
        <Route path="training" element={<Screen perms={["training.read"]}><TrainingPage /></Screen>} />
        <Route path="certification" element={<Screen perms={["cert.learn"]}><CertificationPage /></Screen>} />
        <Route path="privacy" element={<Screen perms={["access.log.read"]}><PrivacyPage /></Screen>} />
        {/* Not /account: console-web serves the customer portal there. */}
        <Route path="profile" element={<AccountPage />} />
        <Route path="settings/security" element={<Screen perms={["keys.manage"]}><KeystorePage /></Screen>} />
        <Route path="training/courses/:courseId" element={<Screen perms={["training.read"]}><CoursePage /></Screen>} />
        <Route path="training/lessons/:lessonId" element={<Screen perms={["training.read"]}><LessonPage /></Screen>} />
        <Route path="kb" element={<Screen perms={["kb.read"]}><KbPage /></Screen>} />
        <Route path="assistant" element={<Screen perms={[]}><AssistantPage /></Screen>} />
        <Route path="assistant/:chatId" element={<Screen perms={[]}><AssistantPage /></Screen>} />
        <Route path="settings/ai" element={<Screen perms={["ai.admin"]}><AiPage /></Screen>} />
        <Route path="admin/*" element={<Screen perms={["iam.users.manage"]}><AdminPage /></Screen>} />
        {/* Not under /admin: the support role reads the audit log without
            managing users. */}
        <Route path="audit" element={<Screen perms={["audit.read"]}><AuditLogPage /></Screen>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <ColorModeProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>
            <AuthProvider>
              <Routed />
            </AuthProvider>
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </ColorModeProvider>
  );
}
